import { resolveMediaKind } from "@utils/media/mediaUrl";
import { parseBatchAudioZip, sanitizeBatchAudioFilenamePart } from "@utils/mode_tool/batchAudioZip";
import { useState } from "react";

const AUDIO_MIME_BY_EXTENSION = {
  mp3: "audio/mpeg",
  wav: "audio/wav",
  ogg: "audio/ogg",
  aac: "audio/aac",
  m4a: "audio/mp4",
  flac: "audio/flac",
  webm: "audio/webm",
};

function getModePointEntries(mode, modePoints, walkingPointsByRoute) {
  if (mode === "walking") {
    return Object.entries(walkingPointsByRoute || {}).flatMap(([routeKey, routePoints]) =>
      (Array.isArray(routePoints) ? routePoints : []).map((point) => ({ point, routeKey })),
    );
  }
  return (Array.isArray(modePoints) ? modePoints : []).map((point) => ({ point, routeKey: "" }));
}

function getEntryExtension(filename = "") {
  const extension = String(filename).split("/").pop()?.match(/\.([^.]+)$/)?.[1];
  const normalized = String(extension || "").toLowerCase();
  return AUDIO_MIME_BY_EXTENSION[normalized] ? normalized : "";
}

function getBaseFilename(path = "") {
  return String(path).split(/[\\/]/).pop() || "";
}

function matchesExportedName(filename, pointId, language, mode, tourName) {
  const safeId = sanitizeBatchAudioFilenamePart(pointId);
  const safeTour = sanitizeBatchAudioFilenamePart(tourName || "tour");
  const extension = getEntryExtension(filename);
  if (!extension) return false;
  const base = getBaseFilename(filename).replace(/\.[^.]+$/, "");
  const prefix = `${safeId}-${language}-`;
  const suffix = `-${mode}-${safeTour}`;
  if (!base.startsWith(prefix)) return false;
  const afterPointName = base.slice(prefix.length);
  if (!afterPointName.includes(suffix)) return false;
  const pointName = afterPointName.slice(0, afterPointName.lastIndexOf(suffix));
  const extraSuffix = afterPointName.slice(pointName.length);
  return Boolean(pointName) && (
    extraSuffix === suffix ||
    extraSuffix === `${suffix}-${language}` ||
    new RegExp(`^${suffix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}-${language}-\\d+$`).test(extraSuffix)
  );
}

export default function useBatchAudioUpload({
  selectedMode = "",
  activeLanguageIso = "",
  modePoints = [],
  walkingPointsByRoute = {},
  tourName = "",
  onUploadPointMedia,
  onCommitPoint,
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  function clearFeedback() {
    setStatus("");
    setError("");
  }

  async function uploadBatchAudios(zipFile) {
    if (isUploading) return;
    setError("");
    setStatus("");
    if (!zipFile || !/\.zip$/i.test(zipFile.name || "")) {
      setError("Choose a ZIP file containing the modified route audios.");
      return;
    }

    const mode = String(selectedMode || "").toLowerCase();
    const language = String(activeLanguageIso || "").toLowerCase().trim();
    if (!mode || !language) {
      setError("Select a route type and language before uploading audios.");
      return;
    }

    setIsUploading(true);
    let committedCount = 0;
    try {
      const archiveEntries = await parseBatchAudioZip(zipFile);
      const pointEntries = getModePointEntries(mode, modePoints, walkingPointsByRoute)
        .filter(({ point }) => point?.id || point?.internalId)
        .sort((left, right) =>
          String(right.point?.id || right.point?.internalId || "").length -
          String(left.point?.id || left.point?.internalId || "").length,
        );
      const matchedEntries = archiveEntries.flatMap((archiveEntry) => {
        const filename = getBaseFilename(archiveEntry.name);
        const matchingPoint = pointEntries.find(({ point }) =>
          matchesExportedName(
            filename,
            String(point?.id || point?.internalId || ""),
            language,
            mode,
            tourName,
          ),
        );
        return matchingPoint ? [{ ...archiveEntry, ...matchingPoint }] : [];
      });

      if (!matchedEntries.length) {
        throw new Error(
          `No ${language.toUpperCase()} ${mode} audios for this tour matched the ZIP filenames.`,
        );
      }
      const targetKeys = matchedEntries.map(({ point }) => String(point.id || point.internalId));
      if (new Set(targetKeys).size !== targetKeys.length) {
        throw new Error(
          "The ZIP contains more than one audio for a point and language. Keep only one file per point before uploading.",
        );
      }
      if (typeof onUploadPointMedia !== "function" || typeof onCommitPoint !== "function") {
        throw new Error("The route audio upload flow is unavailable.");
      }

      let completed = 0;
      setStatus(`Uploading 0 of ${matchedEntries.length} audios…`);
      for (const entry of matchedEntries) {
        const originalName = getBaseFilename(entry.name);
        const extension = getEntryExtension(originalName);
        const mimeType = AUDIO_MIME_BY_EXTENSION[extension];
        const file = new File([entry.blob], originalName, { type: mimeType });
        const uploadedMedia = await onUploadPointMedia([file], {
          language,
          mediaRole: "pointAudio",
        });
        if (!Array.isArray(uploadedMedia) || !uploadedMedia.length) {
          throw new Error(`The audio for point ${entry.point.id || entry.point.internalId} was not uploaded.`);
        }

        const currentPoint = entry.point;
        const nextMedia = [
          ...(Array.isArray(currentPoint?.multimedia) ? currentPoint.multimedia : []).filter(
            (media) => !(
              resolveMediaKind(media) === "audio" &&
              String(media?.language || media?.languageIso || "").toLowerCase() === language
            ),
          ),
          ...uploadedMedia,
        ];
        const nextAudios = [
          ...(Array.isArray(currentPoint?.audios) ? currentPoint.audios : []).filter(
            (audio) => String(audio?.language || audio?.languageIso || "").toLowerCase() !== language,
          ),
          ...uploadedMedia.map((media) => ({
            id: media.sourceId || media.id || "",
            url: media.url || "",
            language,
          })),
        ];
        await onCommitPoint(entry.routeKey, currentPoint.id || currentPoint.internalId, {
          ...currentPoint,
          multimedia: nextMedia,
          audios: nextAudios,
        });
        completed += 1;
        committedCount = completed;
        setStatus(`Uploading ${completed} of ${matchedEntries.length} audios…`);
      }
      setStatus(
        `${completed} ${language.toUpperCase()} audios added to the route draft. Save the route to persist them.`,
      );
    } catch (uploadError) {
      const partialMessage = committedCount
        ? ` ${committedCount} audio(s) were already added to the draft; save the route to persist those changes.`
        : "";
      setError(`${uploadError?.message || "Could not upload the batch audio ZIP."}${partialMessage}`);
      setStatus("");
    } finally {
      setIsUploading(false);
    }
  }

  return { uploadBatchAudios, isUploading, status, error, clearFeedback };
}
